import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Tag, Radio, Badge, Space, Divider, Switch } from 'antd';
import { SearchOutlined, RocketOutlined, BulbOutlined, BulbFilled } from '@ant-design/icons';
import { useChat } from '../context/ChatContext';
import { AGE_OPTIONS, GENDERS, SEARCH_GENDERS } from '../constants';

const { Title, Text } = Typography;

const Setup = () => {
    const { totalOnline, stats, joinPool, darkMode, toggleDarkMode, connected } = useChat();
    
    const [gender, setGender] = useState(localStorage.getItem('chat_gender') || 'male');
    const [ageRange, setAgeRange] = useState(localStorage.getItem('chat_age') || '22-25');
    const [searchAgeRange, setSearchAgeRange] = useState(localStorage.getItem('chat_searchAge') || 'any');
    const [searchGender, setSearchGender] = useState(localStorage.getItem('chat_searchGender') || 'any');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        localStorage.setItem('chat_gender', gender);
        localStorage.setItem('chat_age', ageRange);
        localStorage.setItem('chat_searchAge', searchAgeRange);
        localStorage.setItem('chat_searchGender', searchGender);
    }, [gender, ageRange, searchAgeRange, searchGender]);

    const handleJoin = () => {
        setLoading(true);
        joinPool({ 
            gender, age: ageRange, 
            search_gender: searchGender, search_age: searchAgeRange
        });
    };

    const renderGenderOptions = (options) => options.map(g => (
        <Radio.Button key={g.value} value={g.value} style={{ flex: 1, textAlign: 'center', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Space size={4}>
                {g.icon}
                <span className="hide-mobile">{g.label}</span>
            </Space>
        </Radio.Button>
    ));

    return (
        <div style={{ padding: '20px 10px' }}>
            {/* Hidden SEO Content */}
            <div style={{ display: 'none' }}>
                <h1>Անանուն Չատ - Շփվեք անծանոթների հետ</h1>
                <p>Բարի գալուստ հայկական լավագույն անանուն չատ: Այստեղ կարող եք գտնել նոր ընկերներ, զրուցել հետաքրքիր թեմաների շուրջ և անցկացնել հաճելի ժամանակ: Մեր չատը լիովին անանուն է և չի պահանջում գրանցում:</p>
                <h2>Ինչու՞ օգտվել մեր անանուն չատից</h2>
                <ul>
                    <li>Անվճար և արագ միացում</li>
                    <li>Հայալեզու լսարան</li>
                    <li>Անվտանգ հաղորդակցություն</li>
                    <li>Հարմարավետ մոբայլ տարբերակ</li>
                </ul>
            </div>

            <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <Space>
                    <BulbOutlined style={{ color: darkMode ? '#8c8c8c' : '#faad14' }} />
                    <Switch checked={darkMode} onChange={toggleDarkMode} size="small" />
                    <BulbFilled style={{ color: darkMode ? '#faad14' : '#8c8c8c' }} />
                </Space>
            </div>
            
            <Card className="setup-card" style={{ 
                maxWidth: 600, 
                margin: '0 auto', 
                borderRadius: 20,
                boxShadow: darkMode ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)',
                border: 'none'
            }}>
                <div style={{ textAlign: 'center', marginBottom: 30 }}>
                    <RocketOutlined style={{ fontSize: '40px', color: '#1677ff', marginBottom: 15 }} />
                    <Title level={2} style={{ margin: 0 }}>Անանուն Չատ</Title>
                    <div style={{ marginTop: 15, display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Badge status="processing" text={<Text type="secondary">Օնլայն:</Text>} />
                            <Tag color="processing" style={{ borderRadius: 10, marginLeft: 8, fontWeight: 'bold' }}>{totalOnline}</Tag>
                        </div>
                        <Divider type="vertical" style={{ height: '14px', margin: '0 5px' }} />
                        <Tag color={connected ? 'success' : 'error'} style={{ borderRadius: 10, margin: 0 }}>
                            {connected ? 'Միացված է' : 'Միացման խնդիր'}
                        </Tag>
                    </div>
                </div>
                
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                        <Text strong type="secondary" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Անձնական տվյալներ</Text>
                        <div style={{ display: 'flex', gap: '15px', marginTop: 10, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 200px' }}>
                                <Text style={{ fontSize: '14px' }}>Ձեր սեռը</Text>
                                <Radio.Group value={gender} onChange={e => setGender(e.target.value)} buttonStyle="solid" style={{ marginTop: 8, width: '100%', display: 'flex' }}>
                                    {renderGenderOptions(GENDERS)}
                                </Radio.Group>
                            </div>
                            <div style={{ flex: '1 1 200px' }}>
                                <Text style={{ fontSize: '14px' }}>Ձեր տարիքը</Text>
                                <Radio.Group value={ageRange} onChange={e => setAgeRange(e.target.value)} buttonStyle="solid" style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {AGE_OPTIONS.map(opt => (
                                        <Radio.Button key={opt.value} value={opt.value} style={{ flex: '1 0 30%', textAlign: 'center', borderRadius: 4, height: '35px', lineHeight: '33px', fontSize: '13px' }}>
                                            {opt.label}
                                        </Radio.Button>
                                    ))}
                                </Radio.Group>
                            </div>
                        </div>
                    </div>

                    <Divider style={{ margin: '10px 0' }} />

                    <div>
                        <Text strong type="secondary" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px' }}>Որոնման նախասիրություններ</Text>
                        <div style={{ display: 'flex', gap: '15px', marginTop: 10, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 200px' }}>
                                <Text style={{ fontSize: '14px' }}>Զրուցակցի սեռը</Text>
                                <Radio.Group value={searchGender} onChange={e => setSearchGender(e.target.value)} buttonStyle="solid" style={{ marginTop: 8, width: '100%', display: 'flex' }}>
                                    {renderGenderOptions(SEARCH_GENDERS)}
                                </Radio.Group>
                            </div>
                            <div style={{ flex: '1 1 200px' }}>
                                <Text style={{ fontSize: '14px' }}>Զրուցակցի տարիքը</Text>
                                <Radio.Group value={searchAgeRange} onChange={e => setSearchAgeRange(e.target.value)} buttonStyle="solid" style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    <Radio.Button value="any" style={{ flex: '1 0 30%', textAlign: 'center', borderRadius: 4, height: '35px', lineHeight: '33px', fontSize: '13px' }}>Բոլորը</Radio.Button>
                                    {AGE_OPTIONS.map(opt => (
                                        <Radio.Button key={opt.value} value={opt.value} style={{ flex: '1 0 30%', textAlign: 'center', borderRadius: 4, height: '35px', lineHeight: '33px', fontSize: '13px' }}>
                                            <Badge count={stats[opt.value] || 0} offset={[10, -5]} size="small" color="#52c41a">
                                                {opt.label}
                                            </Badge>
                                        </Radio.Button>
                                    ))}
                                </Radio.Group>
                            </div>
                        </div>
                    </div>

                    <Button 
                        type="primary" 
                        size="large" 
                        block 
                        icon={<SearchOutlined />} 
                        onClick={handleJoin} 
                        loading={loading} 
                        style={{ 
                            height: '55px', 
                            fontSize: '18px', 
                            borderRadius: 12, 
                            marginTop: 15,
                            boxShadow: '0 4px 12px rgba(22, 119, 255, 0.3)',
                            fontWeight: 'bold'
                        }}
                    >
                        Սկսել որոնումը
                    </Button>
                </Space>
                
                <style>{`
                    @media (max-width: 480px) {
                        .hide-mobile { display: none; }
                        .setup-card .ant-card-body { padding: 15px !important; }
                    }
                `}</style>
            </Card>
        </div>
    );
};

export default Setup;